// Auto-generated industries data — DO NOT EDIT MANUALLY
// Slugs: real-estate, hvac-plumbing, legal-services, dental-healthcare
/* eslint-disable max-lines -- Static industry catalog; preserve one reviewed data registry. */
import type { Industry } from "../types";

export const industriesGroup1: Industry[] = [
  {
    slug: "real-estate",
    name: "Real Estate",
    icon: "House",
    tagline: "Never Miss a Buyer Call — 24/7 AI Lead Response",
    description:
      "BizRnR is a 24/7 AI receptionist for real estate agents. It can answer calls, collect buyer or seller intent, follow configured qualification questions, and offer showing or consultation times.",
    seoTitle: "Real Estate Call Intake Guide for AI Receptionists | BizRnR",
    seoDescription:
      "Evidence-backed guide to buyer and seller call intake, qualification fields, scheduling boundaries, property-fact limits, and human handoff.",
    longDescription:
      "Real estate clients call while agents are at showings, driving between properties, or working outside office hours. BizRnR provides an AI receptionist that can answer those calls, identify whether the caller is a buyer, seller, renter, or existing client, and follow qualification questions configured by the agent.\n\nFor buyer inquiries, the workflow can collect budget range, financing or pre-approval status, preferred areas, property type, and timing. For seller inquiries, it can collect the property address, reason for selling, desired timeline, and the best time for a consultation. The receptionist can then offer an available calendar time or route the call according to the team's rules.\n\nThe National Association of Realtors reports that 88% of buyers and 90% of sellers in its 2024 profile used a real estate agent or broker. Those figures establish the continuing importance of agent-led service; they do not measure BizRnR performance. The source is linked on this page so readers and answer engines can verify the context directly.\n\nBizRnR starts at $99 per month and includes a 30-day money-back guarantee. Product capabilities and external market evidence are kept separate: public sources support market statements, while BizRnR's pricing and product pages support its own feature and subscription claims. Agents remain responsible for property facts, fair-housing compliance, representation disclosures, and advice that requires a licensed professional.",
    painPoints: [
      {
        title: "Leads Go Cold Fast",
        description:
          "Real estate leads lose interest within minutes. Manual follow-up is too slow.",
      },
      {
        title: "After-Hours Inquiries Missed",
        description:
          "Calls can arrive while agents are at showings or away from the phone.",
      },
      {
        title: "Time Wasted on Unqualified Leads",
        description:
          "Agents spend hours calling tire-kickers instead of serious buyers.",
      },
    ],
    solutions: [
      {
        title: "Instant AI Voice Response",
        description:
          "The configured AI receptionist can answer forwarded inbound calls 24/7.",
      },
      {
        title: "Smart Lead Qualification",
        description:
          "BizRnR asks timeline, budget, and pre-approval status upfront.",
      },
      {
        title: "Auto-Scheduled Showings",
        description:
          "Qualified leads get booked into your calendar automatically.",
      },
    ],
    stats: [
      {
        value: "24/7",
        label: "Configured call coverage",
      },
      {
        value: "$99",
        label: "Starting monthly price",
      },
      {
        value: "30 days",
        label: "Money-back guarantee",
      },
    ],
    useCases: [
      {
        title: "Listing Inquiry Response",
        description:
          "A buyer calls while the agent is unavailable. BizRnR can collect the listing, timing, and contact details and offer an approved calendar time.",
      },
      {
        title: "Open House Follow-Up",
        description:
          "BizRnR preserves configured intake and call records so the agent can prioritize appropriate follow-up.",
      },
    ],
    faqs: [
      {
        question: "Does BizRnR integrate with my CRM?",
        answer:
          "BizRnR can create a structured call record without requiring a real-estate CRM. Treat a native Follow Up Boss, kvCORE, Sierra Interactive, BoomTown, or other connector as available only when the currently deployed product and customer-facing documentation verify it.",
      },
      {
        question: "Can I customize the AI script?",
        answer:
          "Absolutely. Set your agent name, personality, and talking points.",
      },
      {
        question: "What if a lead wants a human?",
        answer:
          "BizRnR transfers hot leads to you instantly via live call transfer.",
      },
      {
        question: "Is there a contract?",
        answer:
          "No long-term contracts. AI Receptionist starts at $99/month with a 30-day money-back guarantee.",
      },
    ],
    keywords: [
      "real estate AI",
      "real estate lead automation",
      "AI voice agent real estate",
    ],
  },
  {
    slug: "hvac-plumbing",
    name: "HVAC & Plumbing",
    icon: "Wrench",
    tagline: "Never Miss an Emergency Service Call Again",
    description:
      "BizRnR gives HVAC and plumbing companies an AI dispatcher that answers every call and books service appointments automatically.",
    seoTitle: "AI Answering Service for HVAC & Plumbing — $99/mo | BizRnR",
    seoDescription:
      "BizRnR is the 24/7 AI answering service and dispatcher for HVAC & plumbing companies — it answers every call, triages emergencies to your on-call tech, and books service jobs automatically. $99/mo flat, 30-day money-back guarantee.",
    longDescription:
      "The HVAC and plumbing industry loses an estimated $4.8 billion annually in missed calls alone. When a customer's air conditioning fails in July or a pipe bursts at midnight, they call the first company they find — and if nobody answers, they call the next one. BizRnR ensures your HVAC or plumbing business never misses another service call by deploying an AI voice agent that answers every phone call instantly, 24/7/365.\n\nBizRnR's AI dispatcher handles the entire intake process: it gathers the customer's name, address, and problem description, assesses urgency (emergency vs routine), and schedules the appropriate service window. For emergencies, the AI immediately pages your on-call technician while keeping the customer informed. For routine maintenance requests, it books the next available slot and sends a confirmation text.\n\nFollow-up is where most HVAC companies lose revenue. You send a quote, the customer says they'll think about it, and you never hear back. BizRnR eliminates this by automatically calling every open estimate on a scheduled cadence — 24 hours, 3 days, and 7 days after the quote is sent. This automated persistence converts an additional 15-25% of estimates that would otherwise go cold.\n\nSeasonal maintenance is another massive revenue opportunity that most companies leave on the table. BizRnR proactively calls your existing customer base before peak seasons — furnace tune-ups in fall, AC maintenance in spring — filling your schedule during traditionally slow periods.\n\nWith BizRnR, HVAC and plumbing companies typically recapture 30+ missed calls per month and see a 2.4x increase in booked service appointments.",
    painPoints: [
      {
        title: "Missed Calls = Lost Revenue",
        description: "Every missed call is a job going to a competitor.",
      },
      {
        title: "Estimate Follow-Up Falling Through",
        description: "You send estimates but never hear back.",
      },
      {
        title: "After-Hours Emergency Demand",
        description: "Plumbing emergencies do not follow business hours.",
      },
    ],
    solutions: [
      {
        title: "24/7 AI Dispatcher",
        description:
          "BizRnR answers every call and schedules service visits automatically.",
      },
      {
        title: "Estimate Follow-Up Sequences",
        description:
          "Automated SMS and voice follow-ups on every open estimate.",
      },
      {
        title: "Emergency Triage",
        description:
          "AI identifies urgent calls and routes to your on-call tech.",
      },
    ],
    stats: [
      {
        value: "100%",
        label: "Calls answered 24/7",
      },
      {
        value: "2.8x",
        label: "More estimates converted",
      },
      {
        value: "41%",
        label: "Reduction in no-shows",
      },
    ],
    useCases: [
      {
        title: "Emergency Leak at 2am",
        description:
          "Customer calls about a burst pipe. BizRnR answers and pages your on-call plumber.",
      },
      {
        title: "AC Tune-Up Campaign",
        description:
          "BizRnR calls past customers to book seasonal maintenance automatically.",
      },
    ],
    faqs: [
      {
        question:
          "Is BizRnR an AI answering service for HVAC and plumbing companies?",
        answer:
          "Yes. BizRnR is a 24/7 AI answering service built for HVAC and plumbing businesses. It answers every inbound call instantly — including the evening, weekend, and overflow calls that normally go to voicemail — captures the customer's name, address, and problem, and books the right service window. Unlike a generic answering service, it is trained on HVAC and plumbing intake so it knows the difference between a routine tune-up and a no-heat emergency.",
      },
      {
        question:
          "Can the AI dispatch emergency HVAC and plumbing calls after hours?",
        answer:
          "Yes. BizRnR triages every call for urgency. When someone calls about a burst pipe at 2am or a furnace that's out in January, the AI recognizes the emergency, collects the critical details, and immediately pages your on-call technician while keeping the customer informed — so you never lose an emergency job to the next company on the list.",
      },
      {
        question:
          "How much does an AI answering service for HVAC cost with BizRnR?",
        answer:
          "BizRnR is a flat $99/month — no per-call or per-minute fees, even when call volume spikes during a heat wave or cold snap. That's a fraction of a live answering service (which bills $1–$3+ per call) or a full-time receptionist. Every plan includes a 30-day money-back guarantee.",
      },
      {
        question: "Will the AI book service appointments and send reminders?",
        answer:
          "Yes. BizRnR books service visits directly into your schedule with the customer's address and problem description, then sends automated SMS and voice reminders — cutting no-shows by 41%. It also follows up on open estimates on a 24-hour, 3-day, and 7-day cadence, converting 15–25% of quotes that would otherwise go cold.",
      },
      {
        question:
          "Can BizRnR handle calls for multiple technicians and locations?",
        answer:
          "Yes. It integrates with your scheduling system and routes jobs to the right technician or crew based on availability and service area, so a multi-truck HVAC or plumbing operation keeps every job assigned without a dispatcher tied to the phone.",
      },
      {
        question: "What happens with complex technical questions?",
        answer:
          "BizRnR answers common questions about services, pricing ranges, service areas, and availability, and cleanly escalates genuinely technical or unusual requests to a human — capturing all the context first so your tech isn't starting from scratch.",
      },
      {
        question: "How quickly can an HVAC or plumbing company get started?",
        answer:
          "Most HVAC and plumbing companies are live within 24 hours. Setup uses a pre-built trades template, so call answering, emergency triage, and appointment booking work on day one with no scripting required.",
      },
    ],
    keywords: [
      "AI answering service for HVAC",
      "AI answering service for plumbing",
      "HVAC answering service",
      "AI dispatcher HVAC",
      "AI receptionist for HVAC",
      "plumbing lead automation",
      "after hours HVAC answering service",
    ],
  },
  {
    slug: "legal-services",
    name: "Legal Services",
    icon: "Scales",
    tagline: "Intake More Clients. Spend Less Time on the Phone.",
    description:
      "BizRnR automates client intake for law firms, qualifying prospects, collecting case details, and scheduling consultations.",
    longDescription:
      "Law-firm intake requires speed, consistency, and careful limits. BizRnR can answer calls outside office hours, collect the caller's contact information and matter type, ask firm-approved intake questions, and offer a consultation time or route an urgent call. It does not give legal advice, determine whether a firm should accept a matter, or complete a conflict check.\n\nThe American Bar Association's Model Rule 1.4 addresses a lawyer's duty to keep clients reasonably informed and respond to reasonable requests for information. Model Rule 1.6 addresses confidentiality and requires lawyers to make reasonable efforts to prevent unauthorized disclosure or access. The official ABA rules are linked on this page as professional-responsibility context; each firm remains responsible for the rules and laws that apply in its jurisdiction.\n\nA legal intake workflow can be configured by practice area and may collect the prospective client's name, opposing-party names for later conflict review, general matter category, relevant dates, urgency, and preferred consultation time. Sensitive questions should be limited to what the firm has approved, and a lawyer or authorized staff member should review the resulting intake before relying on it.\n\nBizRnR starts at $99 per month with a 30-day money-back guarantee. The platform creates a structured call record and can schedule through supported calendars. Firms should configure recording disclosures, retention, escalation, and access controls with counsel and their own compliance policies.",
    painPoints: [
      {
        title: "High Intake Volume, Low Conversion",
        description:
          "Potential clients call, leave voicemails, and hire someone else.",
      },
      {
        title: "Paralegals Buried in Intake",
        description: "Your legal staff spends hours on intake calls.",
      },
      {
        title: "Off-Hours Inquiries Ignored",
        description: "Accidents and legal emergencies happen at night.",
      },
    ],
    solutions: [
      {
        title: "Automated Client Intake",
        description:
          "AI conducts intake interviews and qualifies leads before your attorney.",
      },
      {
        title: "Consultation Scheduling",
        description:
          "Qualified prospects booked directly into attorney calendars.",
      },
      {
        title: "24/7 Availability",
        description:
          "Never miss a potential client because your office is closed.",
      },
    ],
    stats: [
      {
        value: "24/7",
        label: "Configured intake coverage",
      },
      {
        value: "$99",
        label: "Starting monthly price",
      },
      {
        value: "30 days",
        label: "Money-back guarantee",
      },
    ],
    useCases: [
      {
        title: "Personal Injury Intake",
        description:
          "Accident victim calls at midnight. AI collects details and schedules a morning consultation.",
      },
      {
        title: "Family Law Screening",
        description:
          "AI screens divorce inquiries, gathering key facts before the attorney.",
      },
    ],
    faqs: [
      {
        question: "Is BizRnR bar compliant?",
        answer:
          "No software can make a law firm compliant by itself. Firms should evaluate BizRnR under applicable state-bar rules, privacy law, recording-consent requirements, and their own confidentiality and supervision duties.",
      },
      {
        question: "Can the AI handle sensitive conversations?",
        answer:
          "The firm can configure limited, approved intake questions and escalation rules. The AI does not provide legal advice, determine whether the firm should accept a matter, or replace attorney supervision and confidentiality review.",
      },
      {
        question: "Can I review intake transcripts?",
        answer:
          "Every conversation is transcribed and stored in your dashboard.",
      },
      {
        question: "Does it work for multiple practice areas?",
        answer: "Yes, configure separate intake flows for each practice area.",
      },
    ],
    keywords: [
      "law firm intake automation",
      "legal AI receptionist",
      "attorney lead generation",
    ],
  },
  {
    slug: "dental-healthcare",
    name: "Dental & Healthcare",
    icon: "Tooth",
    tagline: "Fill Your Schedule. Reduce No-Shows. Grow Your Practice.",
    description:
      "BizRnR helps dental and medical practices automate appointment booking, reminders, and patient reactivation via AI.",
    longDescription:
      "Dental practices lose an average of 15 new patients per month to missed phone calls during lunch hours, evenings, and weekends. In an industry where the lifetime value of a single patient can exceed $10,000, every unanswered call represents significant lost revenue. BizRnR gives dental and healthcare practices an AI receptionist that handles patient communications professionally around the clock.\n\nBizRnR's AI answers calls with a warm, professional greeting customized to your practice. It handles appointment scheduling by checking real-time availability in your practice management system, books new patient exams, and manages cancellation waitlists. When patients call about insurance questions, the AI provides general information and routes complex questions to your billing team during business hours.\n\nNew patient acquisition is where BizRnR shines brightest for dental practices. When someone searches \"dentist near me\" and calls your office at 8 PM, BizRnR answers immediately, collects their information, addresses common concerns about first visits, and books them into your next available new patient slot. Practices using BizRnR report a 45% reduction in no-shows thanks to automated appointment confirmations and reminder calls.\n\nFor multi-location dental groups, BizRnR routes calls intelligently based on the patient's ZIP code, preferred location, and insurance network. Emergency dental calls are prioritized and routed to the on-call provider.\n\nBizRnR integrates with Dentrix, Eaglesoft, Open Dental, and other major practice management systems. Every interaction is documented for HIPAA-ready record-keeping.",
    painPoints: [
      {
        title: "Empty Appointment Slots",
        description: "Cancellations leave gaps costing hundreds per hour.",
      },
      {
        title: "No-Show Epidemic",
        description:
          "Without reminders, no-show rates climb costing thousands monthly.",
      },
      {
        title: "Inactive Patient Reactivation",
        description: "Hundreds of patients have not booked in over a year.",
      },
    ],
    solutions: [
      {
        title: "Automated Appointment Reminders",
        description:
          "BizRnR sends AI voice and SMS reminders at 72h, 24h, and 2h.",
      },
      {
        title: "Waitlist Auto-Fill",
        description:
          "When a patient cancels, BizRnR calls your waitlist to fill the slot.",
      },
      {
        title: "Patient Reactivation Campaigns",
        description:
          "AI identifies overdue patients and reaches out to re-book.",
      },
    ],
    stats: [
      {
        value: "45%",
        label: "Reduction in no-shows",
      },
      {
        value: "91%",
        label: "Waitlist fill rate",
      },
      {
        value: "2.4x",
        label: "Reactivation success rate",
      },
    ],
    useCases: [
      {
        title: "Morning Cancellation Recovery",
        description:
          "A patient cancels at 8am. BizRnR calls the next 10 waitlist patients.",
      },
      {
        title: "Annual Cleaning Reactivation",
        description:
          "BizRnR identifies patients 12+ months overdue and calls them.",
      },
    ],
    faqs: [
      {
        question: "Does BizRnR integrate with dental software?",
        answer: "Yes, integrates with major PMS platforms via API.",
      },
      {
        question: "Is patient data secure?",
        answer: "Yes, enterprise-grade encryption and strict protocols.",
      },
      {
        question: "Can I customize reminder messages?",
        answer: "Yes, fully customizable to match your practice voice.",
      },
      {
        question: "How do patients confirm via AI?",
        answer:
          "Patients confirm by replying to SMS or pressing a key during the call.",
      },
    ],
    keywords: [
      "dental appointment reminders AI",
      "healthcare automation",
      "patient reactivation software",
    ],
  },
];
